import styled from "@emotion/styled";
import { useAtomValue, useSetAtom } from "jotai";
// Animations temporarily disabled for performance
// import { AnimatePresence, motion, type Variants } from "motion/react";
import { useCallback, useMemo, type MouseEventHandler, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import { Button, zIndexes } from "@skilitics/designer-ui-components";
import {
  applyResizeScreenDelta,
  OverlayBorder,
  type OverlayGeometry,
  type OverlayPositionStyle,
  type ResizeHandle,
} from "@skilitics/drag-drop";

import { useIsNodeSelected, useIsTriggersOpen, useSetSelectedNodeId } from "../../selection";
import { useDefaultNodeSnapshotHandler, useProvideNodeSnapshotHandler } from "../../snapshots/useSnapshotHandler.ts";
import { DND_CANVAS_EDITOR_ID } from "../dnd";
import { useNodePickingActive } from "../picking";
import { usePickNodeId } from "../picking/useNodePicking";
import {
  canvasHoveredIdAtom,
  canvasIsAnyNodeResizingAtom,
  canvasIsPanningAtom,
  canvasIsTransformOriginDraggingAtom,
  canvasLayoutModeAtom,
  useIsCanvasDragging,
  useIsNodeHovered,
  useNodeResizeState,
} from "./canvasAtoms";
import { ResizeHandles } from "./ResizeHandles.tsx";
import { TransformOriginHandle } from "./TransformOriginHandle.tsx";
import { useCanvasElementOverlayGeometry } from "./useCanvasElementOverlayGeometry.ts";

const ControlsBar = styled.div`
  position: absolute;
  z-index: ${zIndexes.composableNodeControlsBar};
  background: ${({ theme }) => theme.surface};
  font-size: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: -38px;

  box-shadow: ${({ theme }) => theme.boxShadow.normal};
  border-radius: ${({ theme }) => theme.borderRadius}px;

  transform: translateX(-50%);

  ${Button} {
    font-size: inherit;
  }
`;

const Container = styled.div`
  display: contents;
`;

// Borders use SVG so the stroke can be counter-scaled to stay a constant
// device-pixel width regardless of canvas zoom.
const SelectedNodeBorder = styled(OverlayBorder)`
  z-index: ${zIndexes.composableNodeBorderSelected};
  rect {
    stroke: ${({ theme }) => theme.primary};
  }
`;

// Rendered alongside the rounded selection border to show the bounding box that
// resize handles operate on — handles sit on the AABB, which is misleading when
// the element itself is round.
const SelectedBoundingRect = styled(OverlayBorder)`
  z-index: ${zIndexes.composableNodeBorderSelected};
  opacity: 0.5;
  rect {
    stroke: ${({ theme }) => theme.primary};
  }
`;

const HoveredNodeBorder = styled(OverlayBorder)`
  z-index: ${zIndexes.composableNodeBorderHovered};
  rect {
    stroke: ${({ theme }) => theme.primary};
  }
`;

const LayoutModeBorder = styled(OverlayBorder)`
  z-index: ${zIndexes.composableNodeBorderLayout};
  rect {
    stroke: ${({ theme }) => theme.outlineSoft};
  }
`;

const ResizePreviewBorder = styled(OverlayBorder)`
  z-index: ${zIndexes.composableNodeBorderSelected};
  opacity: 0.6;
  rect {
    stroke: ${({ theme }) => theme.primary};
  }
`;

const computeResizePreviewStyle = ({
  geometry,
  resize,
}: {
  geometry: OverlayGeometry;
  resize: { handle: ResizeHandle; delta: { x: number; y: number } };
}): OverlayPositionStyle =>
  applyResizeScreenDelta({ geometry, handle: resize.handle, delta: resize.delta }).overlayStyle;

type Props = {
  children?: ReactNode;
  containerRef: RefObject<HTMLElement | null>;
  hideResizeHandles?: boolean;
  locked?: boolean;
  nodeId: number;
  toolbar?: ReactNode;
  disabled?: boolean;
  hideTransformOrigin?: boolean;
};

export const ComposableWrapper = ({
  children,
  containerRef,
  hideResizeHandles,
  locked,
  nodeId,
  toolbar,
  disabled,
  hideTransformOrigin,
}: Props) => {
  const isTriggersOpen = useIsTriggersOpen();
  const setSelectedNodeId = useSetSelectedNodeId();
  const isSelected = useIsNodeSelected(nodeId);
  const isDragging = useIsCanvasDragging();
  const isCanvasPanning = useAtomValue(canvasIsPanningAtom);

  const setHoveredId = useSetAtom(canvasHoveredIdAtom);
  const isHovered = useIsNodeHovered(nodeId);

  const resizeState = useNodeResizeState(nodeId);
  const isResizing = !!resizeState;
  const isAnyNodeResizing = useAtomValue(canvasIsAnyNodeResizingAtom);

  const isAnyTransformOriginDragging = useAtomValue(canvasIsTransformOriginDraggingAtom);

  const layoutMode = useAtomValue(canvasLayoutModeAtom);

  const pickNodeId = usePickNodeId();
  const nodePickingActive = useNodePickingActive();

  const handler = useDefaultNodeSnapshotHandler(containerRef);
  useProvideNodeSnapshotHandler(nodeId, handler);

  const suspendRectObservation =
    (isDragging && !isAnyNodeResizing && !isAnyTransformOriginDragging) ||
    isCanvasPanning ||
    isTriggersOpen ||
    disabled;
  // Only the wrappers that will actually render an overlay this frame need
  // geometry. Idle wrappers pass `preventUpdate=true` so they attach no
  // observers and skip the snapshot — keeps cost flat at 1000+ wrappers.
  const needsGeometry = isSelected || isHovered || layoutMode || isResizing;
  const geometry = useCanvasElementOverlayGeometry(containerRef, suspendRectObservation || !needsGeometry);
  const { boundingRect } = geometry;

  const canResize = !hideResizeHandles && !locked;
  const enableBorders = !isTriggersOpen;

  const handleMouseOver: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (disabled || isTriggersOpen) return;
      e.stopPropagation();
      setHoveredId(nodeId);
    },
    [nodeId, setHoveredId, disabled, isTriggersOpen],
  );

  const handleMouseOut: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (disabled || isTriggersOpen) return;
      e.stopPropagation();
      setHoveredId(undefined);
    },
    [setHoveredId, disabled, isTriggersOpen],
  );

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();

      if (locked) return;

      if (nodePickingActive) {
        pickNodeId(nodeId);
      } else if (!isSelected) {
        setSelectedNodeId(nodeId);
      }
    },
    [isSelected, locked, nodeId, pickNodeId, nodePickingActive, setSelectedNodeId],
  );

  const editorContainer = document.getElementById(DND_CANVAS_EDITOR_ID);

  const shouldShowBorders = !isCanvasPanning && (!isDragging || isAnyNodeResizing || isAnyTransformOriginDragging);

  const controlsBarStyles = {
    top: boundingRect.top,
    left: boundingRect.left + boundingRect.width / 2,
  };

  const resizePreviewStyle = useMemo(
    () => (isResizing ? computeResizePreviewStyle({ geometry, resize: resizeState }) : undefined),
    [isResizing, geometry, resizeState],
  );

  return (
    <>
      <Container onClick={handleClick} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
        {children}
      </Container>

      {containerRef && enableBorders && editorContainer && !disabled && shouldShowBorders && (
        <>
          {isSelected &&
            toolbar &&
            !isResizing &&
            createPortal(
              <ControlsBar style={controlsBarStyles} onClick={(event) => event.stopPropagation()}>
                {toolbar}
              </ControlsBar>,
              editorContainer,
            )}

          {isSelected && createPortal(<SelectedNodeBorder geometry={geometry} strokeWidth={2} />, editorContainer)}

          {isSelected &&
            canResize &&
            (geometry.borderRadius.rx > 0 || geometry.borderRadius.ry > 0) &&
            createPortal(
              <SelectedBoundingRect geometry={geometry} strokeWidth={1} ignoreBorderRadius />,
              editorContainer,
            )}

          {isSelected &&
            canResize &&
            createPortal(<ResizeHandles nodeId={nodeId} geometry={geometry} />, editorContainer)}

          {isSelected &&
            !hideTransformOrigin &&
            createPortal(
              <TransformOriginHandle nodeId={nodeId} containerRef={containerRef} geometry={geometry} />,
              editorContainer,
            )}

          {isResizing &&
            canResize &&
            resizePreviewStyle &&
            createPortal(
              <ResizePreviewBorder geometry={geometry} overlayStyle={resizePreviewStyle} strokeWidth={2} dashed />,
              editorContainer,
            )}

          {isHovered && createPortal(<HoveredNodeBorder geometry={geometry} strokeWidth={1} dashed />, editorContainer)}

          {layoutMode && createPortal(<LayoutModeBorder geometry={geometry} strokeWidth={1} dashed />, editorContainer)}
        </>
      )}
    </>
  );
};
